import { PaymentSources, PaymentTypes } from '@appTypes/enums';
import type {
  DeleteUnitPaymentLogsRes,
  GetMembersBalanceDetailsReq,
  GetMembersBalanceDetailsRes,
  GetPaymentLogCategories,
  GetUnitPaymentLogsReq,
  GetUnitPaymentLogsRes,
  GetUserPaymentLogsQueryParams,
  GetUserPaymentLogsRes,
  PaymentLogUpdateQuery,
  PostAddPaymentLogCommentsRequest,
  PostAddPaymentLogCommentsResponse,
  PostFetchPaymentLogCommentsRequest,
  PostFetchPaymentLogCommentsResponse,
  PostPaymentLogRequest,
  PostPaymentLogResponse,
  PostProcessPaymentRequest,
  PutUnitPaymentLogsRes,
} from '@appTypes/esb';
import type { PutPaymentLogRequest } from '@modules/paymentLogs/types';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';

export const paymentLogsAPI = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint GET /advancements/{userId}/paymentLogs
     */
    getUserPaymentLogs: builder.query<
      GetUserPaymentLogsRes,
      {
        userId: number;
        params: GetUserPaymentLogsQueryParams;
      }
    >({
      query: arg => ({
        url: `/advancements/${arg.userId}/paymentLogs`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [tagFactory.PaymentLogQuery.userPaymentLog(`${arg.userId}`)]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/organization/{organizationGuid}/paymentLogs
     */
    getUnitPaymentLogs: builder.query<
      GetUnitPaymentLogsRes,
      {
        orgGuid: string;
        params: GetUnitPaymentLogsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/paymentLogs`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              tagFactory.PaymentLogQuery.orgPaymentLogs(
                arg.orgGuid,
                arg.params.paymentType,
              ),
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/{organizationGuid}/paymentLogSummary
     */
    GetUnitPaymentLogsSummary: builder.query<
      GetUnitPaymentLogsRes,
      {
        orgGuid: string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/paymentLogSummary`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [tagFactory.PaymentLogSummary.orgPaymentLogsSummary(arg.orgGuid)]
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/{organizationGuid}/balanceDetails
     */
    GetMembersPaymentLogsBalance: builder.query<
      GetMembersBalanceDetailsRes,
      {
        orgGuid: string;
        params: GetMembersBalanceDetailsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/balanceDetails`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [tagFactory.PaymentLogBalance.orgPaymentLogsBalance(arg.orgGuid)]
          : [],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/users/paymentLogs
     */
    postPaymentLog: builder.mutation<
      PostPaymentLogResponse,
      PostPaymentLogRequest
    >({
      query: body => ({
        url: `/advancements/v2/users/paymentLogs`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];
        const isUnitTransaction = arg.paymentLogs.some(
          el => el.source === PaymentSources.UNIT,
        );

        const userLogsTags = arg.paymentLogs
          .filter(el => el.source === PaymentSources.USER)
          .map(el => tagFactory.PaymentLogQuery.userPaymentLog(`${el.userId}`));

        const categoriesTag =
          tagFactory.PaymentLogCategories.orgPaymentLogsCategories(
            arg.organizationGuid,
          );

        const invalidated = isUnitTransaction
          ? [
              tagFactory.PaymentLogSummary.orgPaymentLogsSummary(
                arg.organizationGuid,
              ),
              tagFactory.PaymentLogQuery.orgPaymentLogs(
                arg.organizationGuid,
                arg.paymentLogs?.[0]?.paymentType,
              ),
              categoriesTag,
            ]
          : [
              tagFactory.PaymentLogSummary.orgPaymentLogsSummary(
                arg.organizationGuid,
              ),
              tagFactory.PaymentLogBalance.orgPaymentLogsBalance(
                arg.organizationGuid,
              ),
              categoriesTag,
              ...userLogsTags,
            ];

        return invalidated;
      },
    }),
    /**
     * Fetches user comments for a unit's payment logs.
     * POST is used as a read, hence `builder.query`.
     * @esbEndpoint POST /advancements/v2/users/paymentLogs/comments
     */
    getPaymentLogComments: builder.query<
      PostFetchPaymentLogCommentsResponse,
      PostFetchPaymentLogCommentsRequest
    >({
      query: body => ({
        url: '/advancements/v2/users/paymentLogs/comments',
        method: 'POST',
        body,
      }),
      // Provided even on empty/error responses so adding the first note of a
      // unit still triggers a refetch
      providesTags: (_result, _error, { unitId }) => [
        tagFactory.PaymentLogComments.forUnit(unitId),
      ],
    }),

    /**
     * Adds single or bulk user comments for payment logs.
     * @esbEndpoint POST /advancements/v2/users/paymentLogs/comments/add
     */
    addPaymentLogComments: builder.mutation<
      PostAddPaymentLogCommentsResponse,
      PostAddPaymentLogCommentsRequest
    >({
      query: body => ({
        url: '/advancements/v2/users/paymentLogs/comments/add',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, error, comments) =>
        error
          ? []
          : [...new Set(comments.map(({ unitId }) => unitId))].map(unitId =>
              tagFactory.PaymentLogComments.forUnit(unitId),
            ),
    }),
    /**
     * @esbEndpoint PUT /advancements/v2/organization/{organizationGuid}/paymentLogs/{paymentLogId}
     */
    putPaymentLog: builder.mutation<
      PutUnitPaymentLogsRes,
      PutPaymentLogRequest
    >({
      query: ({ organizationGuid, paymentLogId, payload }) => ({
        url: `/advancements/v2/organization/${organizationGuid}/paymentLogs/${paymentLogId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (
        _result,
        error,
        { userId, organizationGuid, paymentType },
      ) => {
        if (error) return [];

        const invalidated = [
          tagFactory.PaymentLogSummary.orgPaymentLogsSummary(organizationGuid),
          tagFactory.PaymentLogBalance.orgPaymentLogsBalance(organizationGuid),
          ...(userId
            ? [tagFactory.PaymentLogQuery.userPaymentLog(`${userId}`)]
            : []),
          ...(paymentType
            ? [
                tagFactory.PaymentLogQuery.orgPaymentLogs(
                  organizationGuid,
                  paymentType as PaymentTypes,
                ),
              ]
            : []),
        ];

        return invalidated;
      },
    }),
    /**
     * @esbEndpoint DELETE /advancements/v2/organization/{organizationGuid}/paymentLogs/{paymentLogId}
     */
    deletePaymentLog: builder.mutation<
      DeleteUnitPaymentLogsRes,
      PaymentLogUpdateQuery
    >({
      query: ({ organizationGuid, paymentLogId }) => ({
        url: `/advancements/v2/organization/${organizationGuid}/paymentLogs/${paymentLogId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (
        _result,
        error,
        { userId, organizationGuid, paymentType },
      ) => {
        if (error) return [];

        const paymentTypeTags =
          paymentType === 'All'
            ? [
                tagFactory.PaymentLogQuery.orgPaymentLogs(
                  organizationGuid,
                  PaymentTypes.ONLINE,
                ),
                tagFactory.PaymentLogQuery.orgPaymentLogs(
                  organizationGuid,
                  PaymentTypes.PAYMENT,
                ),
                tagFactory.PaymentLogQuery.orgPaymentLogs(
                  organizationGuid,
                  PaymentTypes.CHARGE,
                ),
              ]
            : [
                tagFactory.PaymentLogQuery.orgPaymentLogs(
                  organizationGuid,
                  paymentType as PaymentTypes,
                ),
              ];

        const invalidated = [
          tagFactory.PaymentLogSummary.orgPaymentLogsSummary(organizationGuid),
          tagFactory.PaymentLogBalance.orgPaymentLogsBalance(organizationGuid),
          ...(userId
            ? [tagFactory.PaymentLogQuery.userPaymentLog(`${userId}`)]
            : []),
          ...(paymentType ? paymentTypeTags : []),
        ];

        return invalidated;
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/{unitId}/paymentLogCategories
     */
    getPaymentLogCategories: builder.query<
      GetPaymentLogCategories,
      { unitId: number; organizationGuid: string }
    >({
      query: ({ unitId }) => ({
        url: `/organizations/v2/units/${unitId}/paymentLogCategories `,
        method: 'GET',
      }),
      providesTags: (result, error, { organizationGuid }) =>
        result && !error
          ? [
              tagFactory.PaymentLogCategories.orgPaymentLogsCategories(
                organizationGuid,
              ),
            ]
          : [],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/organization/{orgGuid}/paymentLogs/{paymentLogId}/processPayment
     */
    processPayment: builder.mutation<
      PostPaymentLogResponse,
      {
        orgGuid: string;
        paymentLogId: string;
        payload: PostProcessPaymentRequest;
      }
    >({
      query: ({ orgGuid, paymentLogId, payload }) => ({
        url: `/advancements/v2/organization/${orgGuid}/paymentLogs/${paymentLogId}/processPayment`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, error, { orgGuid, payload }) => {
        if (error) return [];

        const invalidated = [
          tagFactory.PaymentLogSummary.orgPaymentLogsSummary(orgGuid),
          tagFactory.PaymentLogBalance.orgPaymentLogsBalance(orgGuid),
          tagFactory.PaymentLogQuery.userPaymentLog(`${payload.userId}`),
        ];

        return invalidated;
      },
    }),
  }),
});

export const {
  useGetUserPaymentLogsQuery,
  useLazyGetUserPaymentLogsQuery,
  useGetUnitPaymentLogsQuery,
  useLazyGetUnitPaymentLogsQuery,
  usePostPaymentLogMutation,
  useGetPaymentLogCommentsQuery,
  useLazyGetPaymentLogCommentsQuery,
  useAddPaymentLogCommentsMutation,
  usePutPaymentLogMutation,
  useDeletePaymentLogMutation,
  useGetUnitPaymentLogsSummaryQuery,
  useLazyGetUnitPaymentLogsSummaryQuery,
  useGetMembersPaymentLogsBalanceQuery,
  useGetPaymentLogCategoriesQuery,
  useProcessPaymentMutation,
} = paymentLogsAPI;
